/** Belegungskalender (Screen laden/Belegung): Wochen-/Monatsraster je Regal, Lücken, „Buchung anlegen“ aus einer Lücke. Schließtage sind V2. */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button, EmptyState, Seitenzustand } from "../ui";
import { Card, PageHeader, Select } from "../ui/Layout";
import { formatTag, tagVon } from "@shared/zeit";
import { monatPlus, tagPlus } from "@shared/perioden";
import { useBelegung, usePlan } from "./regale-api";
import { Ladenplan, PlanLegende } from "../ui/Ladenplan";

function montagVon(tag: string): string {
  const d = new Date(`${tag}T00:00:00Z`);
  const wt = (d.getUTCDay() + 6) % 7;
  return tagPlus(tag, -wt);
}
function kw(tag: string): number {
  const d = new Date(`${tag}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 4 - ((d.getUTCDay() + 6) % 7) - 1 + 1);
  const start = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - start.getTime()) / 86_400_000 + 1) / 7);
}

export function Belegung() {
  const navigate = useNavigate();
  const heute = tagVon();
  const [raster, setRaster] = useState<"woche" | "monat">("woche");
  const [anker, setAnker] = useState(heute);
  const [lage, setLage] = useState("");
  const [nurLuecken, setNurLuecken] = useState(false);
  const [ansicht, setAnsicht] = useState<"kalender" | "plan">("kalender");
  const plan = usePlan();

  const spalten = useMemo(() => {
    if (raster === "woche") {
      const start = montagVon(anker);
      return Array.from({ length: 13 }, (_, i) => {
        const von = tagPlus(start, i * 7);
        return { von, bis: tagPlus(von, 6), label: `KW ${kw(von)}`, sub: formatTag(von).slice(0, 6) };
      });
    }
    const start = `${anker.slice(0, 7)}-01`;
    return Array.from({ length: 6 }, (_, i) => {
      const von = monatPlus(start, i);
      const bis = tagPlus(monatPlus(von, 1), -1);
      return { von, bis, label: new Intl.DateTimeFormat("de-DE", { month: "short", timeZone: "UTC" }).format(new Date(`${von}T00:00:00Z`)), sub: von.slice(0, 4) };
    });
  }, [raster, anker]);
  const von = spalten[0]!.von;
  const bis = spalten[spalten.length - 1]!.bis;
  const daten = useBelegung(von, bis, lage || undefined);

  const zeilen = (daten.data?.regale ?? []).map((r) => {
    const buchungen = (daten.data?.buchungen ?? []).filter((b) => b.regalId === r.id);
    const zellen = spalten.map((sp) => {
      const b = buchungen.find((x) => x.start <= sp.bis && (x.ende === null || x.ende >= sp.von));
      return { sp, b, beginnt: !!b && b.start >= sp.von && b.start <= sp.bis };
    });
    return { r, zellen, hatLuecke: zellen.some((z) => !z.b) };
  });
  const sichtbar = nurLuecken ? zeilen.filter((z) => z.hatLuecke) : zeilen;

  return (
    <>
      <PageHeader
        kicker="Regale"
        title="Belegungskalender"
        sub={`${formatTag(von)} bis ${formatTag(bis)}${lage ? ` · ${lage}` : ""}`}
        actions={
          <>
            <button type="button" className="sc-pill" aria-pressed={ansicht === "kalender" && raster === "woche"} onClick={() => { setAnsicht("kalender"); setRaster("woche"); }}>Woche</button>
            <button type="button" className="sc-pill" aria-pressed={ansicht === "kalender" && raster === "monat"} onClick={() => { setAnsicht("kalender"); setRaster("monat"); }}>Monat</button>
            <button type="button" className="sc-pill" aria-pressed={ansicht === "plan"} onClick={() => setAnsicht("plan")}>Plan</button>
            <Button variant="quiet" icon="chevron-left" aria-label="Zurück" onClick={() => setAnker(raster === "woche" ? tagPlus(anker, -28) : monatPlus(anker, -2))} />
            <Button variant="secondary" onClick={() => setAnker(heute)}>Heute</Button>
            <Button variant="quiet" icon="chevron-right" aria-label="Weiter" onClick={() => setAnker(raster === "woche" ? tagPlus(anker, 28) : monatPlus(anker, 2))} />
            <Link to="/laden/buchungen/neu" className="rg-btn rg-btn--primary">Buchung anlegen</Link>
          </>
        }
      />
      {ansicht === "plan" ? (
        <Seitenzustand query={plan} lines={6}>
          {(p) =>
            p.regale.some((r) => r.platz) ? (
              <Card title="Ladenplan heute" actions={<Link to="/laden/regale/plan" className="rg-btn rg-btn--quiet">Plan bearbeiten</Link>}>
                <Ladenplan spalten={p.plan.spalten} zeilen={p.plan.zeilen} elemente={p.plan.elemente} regale={p.regale} hintergrundUrl={p.plan.hintergrundKey ? `/api/laden/dateien/${p.plan.hintergrundKey}` : null} onRegalKlick={(r) => navigate(r.belegung === "frei" ? `/laden/buchungen/neu?regal=${r.id}` : `/laden/regale/${r.id}`)} maxHoehe={640} />
                <div style={{ marginTop: "var(--space-3)" }}><PlanLegende /></div>
                <p className="sc-muted sc-small">Antippen: freies Regal → Buchung anlegen, sonst Regal öffnen.</p>
              </Card>
            ) : (
              <Card><EmptyState compact illustration="empty-shelf" title="Noch kein Ladenplan" action={<Link to="/laden/regale/plan" className="rg-btn rg-btn--primary">Ladenplan anlegen</Link>}>Regale aufs Raster ziehen – danach zeigt der Plan die Belegung farbig.</EmptyState></Card>
            )
          }
        </Seitenzustand>
      ) : null}
      <div className="sc-toolbar" style={{ marginBottom: "var(--space-4)", display: ansicht === "plan" ? "none" : undefined }}>
        <span className="sc-legend"><i style={{ background: "var(--violett)" }} /> Gebucht</span>
        <span className="sc-legend"><i style={{ background: "var(--attention)" }} /> Reserviert, Vertrag offen</span>
        <span className="sc-legend"><i style={{ background: "var(--surface-raised)", border: "1px solid var(--line)" }} /> Frei</span>
        <span className="sc-grow" />
        {daten.data?.lagen.length ? (
          <Select label="Bereich" value={lage} onChange={(e) => setLage(e.target.value)}>
            <option value="">Alle</option>
            {daten.data.lagen.map((l) => <option key={l} value={l}>{l}</option>)}
          </Select>
        ) : null}
        <label className="sc-check"><input type="checkbox" checked={nurLuecken} onChange={(e) => setNurLuecken(e.target.checked)} /> Nur Lücken zeigen</label>
      </div>
      {ansicht === "plan" ? null : (
      <Seitenzustand query={daten} lines={8} istLeer={(d) => d.regale.length === 0} leer={<EmptyState illustration="empty-shelf" title="Noch keine Regale" action={<Link to="/laden/einrichtung" className="rg-btn rg-btn--primary">Regale anlegen</Link>}>Der Kalender zeigt je Regal, wer wann gebucht hat.</EmptyState>}>
        {() => (
          <Card flush>
            <div className="app-tabellen-scroll">
              <table className="rg-table app-belegung">
                <thead>
                  <tr>
                    <th>Regal</th>
                    {spalten.map((sp) => (
                      <th key={sp.von} className={heute >= sp.von && heute <= sp.bis ? "is-heute" : undefined}>
                        {sp.label}
                        <div className="sc-muted" style={{ fontWeight: 400 }}>{heute >= sp.von && heute <= sp.bis ? "heute" : sp.sub}</div>
                        {(() => { const zu = (daten.data?.schliesstage ?? []).filter((z) => z.tag >= sp.von && z.tag <= sp.bis); return zu.length ? <div className="sc-small" style={{ fontWeight: 400, color: "var(--attention)" }} title={zu.map((z) => `${z.tag}: ${z.grund}`).join(", ")}>{zu.length === 1 ? `zu: ${zu[0]!.grund}` : `${zu.length} Schließtage`}</div> : null; })()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sichtbar.length === 0 ? (
                    <tr><td colSpan={spalten.length + 1} className="rg-table__empty">Keine Lücke im Zeitraum.</td></tr>
                  ) : (
                    sichtbar.map(({ r, zellen }) => (
                      <tr key={r.id}>
                        <td><Link to={`/laden/regale/${r.id}`} className="sc-strong">{r.nummer}</Link>{r.lage ? <div className="sc-muted sc-small">{r.lage}</div> : null}</td>
                        {zellen.map(({ sp, b, beginnt }) =>
                          b ? (
                            <td key={sp.von} className={["app-belegung__zelle", b.status === "reserviert" ? "is-reserviert" : "is-gebucht"].join(" ")} title={`${b.mieterName} ${formatTag(b.start)}–${b.ende ? formatTag(b.ende) : "offen"}`} onClick={() => navigate(`/laden/buchungen/${b.id}`)}>
                              {beginnt || sp === spalten[0] ? <span className="app-belegung__name">{b.mieterName}{b.status === "reserviert" ? " · reserviert" : ""}</span> : null}
                            </td>
                          ) : (
                            <td key={sp.von} className="app-belegung__zelle is-frei" onClick={() => navigate(`/laden/buchungen/neu?regal=${r.id}&start=${sp.von > heute ? sp.von : heute}`)} title="Buchung anlegen">
                              <span className="sc-muted">frei</span>
                            </td>
                          ),
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </Seitenzustand>
      )}
    </>
  );
}
